import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import GtDialog from "src/components/Common/GtDialog";
import { CoreInterfaces, DTOs } from "src/core/Models";
import * as Constants from "src/core/Constants";

interface GtExistingLettersDialogProps {
  isOpen: boolean;
  letters: Array<CoreInterfaces.SignedEngagementLetter>;
  onCreateNew: () => void;
  onClose: () => void;
}

function formatSigningDate(signingDate: string): string {
  if (!signingDate) {
    return "---";
  }
  const parsedDate = new Date(signingDate);
  return isNaN(parsedDate.getTime())
    ? "---"
    : parsedDate.toLocaleDateString("sv-SE");
}

function buildEngagementUrl(
  letter: CoreInterfaces.SignedEngagementLetter
): string {
  const params = new URLSearchParams();
  params.set(
    Constants.QueryParams.businessOpportunityId,
    letter.BusinessOpportunityId
  );
  params.set(Constants.QueryParams.clientId, letter.ClientId);
  params.set(Constants.QueryParams.unit, letter.BusinessUnit);
  params.set(Constants.QueryParams.engagementConfigurationId, letter.Id);
  return `/?${params.toString()}`;
}

function GtExistingLettersDialog(
  props: GtExistingLettersDialogProps
): JSX.Element {
  const { isOpen, letters, onCreateNew, onClose } = props;
  const { t } = useTranslation();
  const [selectedLetterId, setSelectedLetterId] = useState<string>(
    letters.length > 0 ? letters[0].Id : ""
  );

  useEffect(() => {
    const isSelectionValid = letters.some(
      (letter) => letter.Id === selectedLetterId
    );
    if (!isSelectionValid) {
      setSelectedLetterId(letters.length > 0 ? letters[0].Id : "");
    }
  }, [letters, selectedLetterId]);

  const handleUpdateSelected = (): void => {
    const selectedLetter = letters.find(
      (letter) => letter.Id === selectedLetterId
    );
    if (selectedLetter) {
      window.open(buildEngagementUrl(selectedLetter), "_blank", "noopener");
    }
  };

  const existingLettersDialogDTO: DTOs.GtDialogDTO = {
    data: {
      maxWidth: "sm",
    },
    api: {
      onClose: onClose,
    },
    state: {
      isOpen: isOpen,
      isFullWidth: true,
    },
  };

  return (
    <GtDialog gtDialogDTO={existingLettersDialogDTO}>
      <article className="gt-existingLettersDialog">
        <Typography
          className="gt-existingLettersDialog__header"
          variant="h6"
          component="h2"
        >
          {t("ExistingLettersDialog.Header")}
        </Typography>
        <Typography className="gt-existingLettersDialog__text">
          {t("ExistingLettersDialog.Text")}
        </Typography>

        <Typography className="gt-existingLettersDialog__subHeader">
          {t("ExistingLettersDialog.SelectLetterSubHeader")}
        </Typography>

        <RadioGroup
          className="gt-existingLettersDialog__letters"
          value={selectedLetterId}
          onChange={(event) => setSelectedLetterId(event.target.value)}
        >
          {letters.map((letter) => (
            <FormControlLabel
              key={letter.Id}
              value={letter.Id}
              className="gt-existingLettersDialog__letters-row"
              control={<Radio />}
              label={
                <span className="gt-existingLettersDialog__letters-row-content">
                  <span className="gt-existingLettersDialog__letters-row-text">
                    <span className="gt-existingLettersDialog__letters-row-type">
                      {t(
                        `ExistingLettersDialog.LetterType.${letter.LetterType}`
                      )}
                    </span>
                    {!!letter.BusinessOpportunityDescription && (
                      <span
                        className="gt-existingLettersDialog__letters-row-description"
                        title={letter.BusinessOpportunityDescription}
                      >
                        {letter.BusinessOpportunityDescription}
                      </span>
                    )}
                  </span>
                  <span className="gt-existingLettersDialog__letters-row-date">
                    {t("ExistingLettersDialog.Signed")}{" "}
                    {formatSigningDate(letter.SigningDate)}
                  </span>
                </span>
              }
            />
          ))}
        </RadioGroup>

        <Button
          className="gt-existingLettersDialog__updateButton"
          variant="contained"
          color="primary"
          size="large"
          disabled={!selectedLetterId}
          onClick={handleUpdateSelected}
        >
          {t("ExistingLettersDialog.UpdateSelectedButton")}
        </Button>

        <Divider className="gt-existingLettersDialog__divider" />

        <Typography className="gt-existingLettersDialog__subHeader">
          {t("ExistingLettersDialog.NewEngagementSubHeader")}
        </Typography>
        <Button
          className="gt-existingLettersDialog__createNewButton"
          variant="outlined"
          color="tertiary"
          size="small"
          onClick={onCreateNew}
        >
          {t("ExistingLettersDialog.CreateNewButton")}
        </Button>
      </article>
    </GtDialog>
  );
}

export default GtExistingLettersDialog;
